import React, { useState, useEffect } from 'react';
import { Search, Users, Check } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'react-hot-toast';
import { Button } from './ui/button';
import UserProfile from './UserProfile';

interface Profile {
  id: string;
  user_id: string;
  username: string;
  display_name?: string;
  bio?: string;
  is_public: boolean;
  is_following?: boolean;
  avatar_url?: string;
}

export default function UserSearch() {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(false);
  const [followLoading, setFollowLoading] = useState<{[key: string]: boolean}>({});
  const [selectedUser, setSelectedUser] = useState<string | null>(null);

  const handleSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const term = e.target.value;
    setSearchTerm(term);
    setSelectedUser(null);

    if (term.length < 3) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .or(`username.ilike.%${term}%,display_name.ilike.%${term}%`)
        .neq('user_id', user?.id)
        .limit(10);

      if (profileError) throw profileError;

      if (profiles) {
        const { data: follows, error: followError } = await supabase
          .from('follows')
          .select('following_id')
          .eq('follower_id', user?.id);

        if (followError) throw followError;

        const followingIds = follows?.map(f => f.following_id) || [];

        const resultsWithFollowStatus = profiles.map(profile => ({
          ...profile,
          is_following: followingIds.includes(profile.user_id)
        }));

        setSearchResults(resultsWithFollowStatus);
      }
    } catch (error) {
      console.error('Error searching users:', error);
      toast.error('Failed to search users');
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async (userId: string) => {
    setFollowLoading(prev => ({ ...prev, [userId]: true }));
    
    try {
      const { data: existingFollow, error: checkError } = await supabase
        .from('follows')
        .select('id')
        .eq('follower_id', user?.id)
        .eq('following_id', userId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') {
        throw checkError;
      }

      if (existingFollow) {
        const { error: unfollowError } = await supabase
          .from('follows')
          .delete()
          .eq('follower_id', user?.id)
          .eq('following_id', userId);

        if (unfollowError) throw unfollowError;
        
        setSearchResults(prev =>
          prev.map(profile =>
            profile.user_id === userId
              ? { ...profile, is_following: false }
              : profile
          )
        );
        
        toast.success('User unfollowed');
      } else {
        const { error: followError } = await supabase
          .from('follows')
          .insert({ follower_id: user?.id, following_id: userId });

        if (followError) throw followError;
        
        setSearchResults(prev =>
          prev.map(profile =>
            profile.user_id === userId
              ? { ...profile, is_following: true }
              : profile
          )
        );
        
        toast.success('User followed successfully!');
      }
    } catch (error: any) {
      console.error('Error following user:', error);
      toast.error('Failed to update follow status');
    } finally {
      setFollowLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  if (selectedUser) {
    return (
      <div>
        <Button
          variant="ghost"
          onClick={() => setSelectedUser(null)}
          className="mb-6"
        >
          ← Back to Search
        </Button>
        <UserProfile userId={selectedUser} />
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto">
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={handleSearch}
          placeholder="Search users..."
          className="w-full px-4 py-3 pl-12 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
      </div>

      {loading && (
        <div className="flex justify-center mt-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        </div>
      )}

      {searchResults.length > 0 && (
        <div className="mt-4 space-y-2">
          {searchResults.map((profile) => (
            <div
              key={profile.id}
              className="bg-white p-4 rounded-lg border border-gray-200"
            >
              <div className="flex items-center justify-between">
                <div 
                  className="flex items-center gap-3 flex-1 cursor-pointer"
                  onClick={() => setSelectedUser(profile.user_id)}
                >
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                    {profile.avatar_url ? (
                      <img
                        src={profile.avatar_url}
                        alt={profile.username}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <span className="font-medium text-primary-600">
                        {profile.username[0].toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="font-medium">{profile.username}</p>
                    {profile.display_name && (
                      <p className="text-sm text-gray-500">{profile.display_name}</p>
                    )}
                  </div>
                </div>
                <Button
                  onClick={() => handleFollow(profile.user_id)}
                  variant={profile.is_following ? "outline" : "default"}
                  disabled={followLoading[profile.user_id]}
                >
                  {followLoading[profile.user_id] ? (
                    <div className="h-5 w-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : profile.is_following ? (
                    'Following'
                  ) : (
                    'Follow'
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {searchTerm.length >= 3 && searchResults.length === 0 && !loading && (
        <div className="text-center mt-8 text-gray-500">
          <Users className="h-12 w-12 mx-auto mb-3 text-gray-400" />
          <p className="text-lg font-medium text-gray-900">No users found</p>
          <p>Try searching with a different term</p>
        </div>
      )}
    </div>
  );
}